// CheerpX runtime loader. The runtime is never bundled or self-hosted (licence): it is imported at
// run time from the pinned CDN URL, once per page (single-flight; a failure can be retried).
// Preconditions are checked first so the user sees a clear message rather than the raw
// "SharedArrayBuffer transfer requires self.crossOriginIsolated". Ported from EndisOS.
import { LinuxError } from "../engine.js";
import { CHEERPX_RUNTIME_HOST } from "../../shared/linux-catalog.js";
import { noCredentialless } from "../../shared/isolation.js";

export function readPreconditionEnv(accepted, g = globalThis) {
	let wasmSharedMemory = false;
	try { new g.WebAssembly.Memory({ initial: 1, maximum: 1, shared: true }); wasmSharedMemory = true; } catch (e) {}
	return {
		secureContext: g.isSecureContext === true,
		crossOriginIsolated: g.crossOriginIsolated === true,
		sharedArrayBuffer: typeof g.SharedArrayBuffer === "function",
		wasmSharedMemory,
		online: !g.navigator || g.navigator.onLine !== false,
		accepted: !!accepted,
		// Safari and iOS: isolation comes only from the server's require-corp on a top-level /linux.
		webkit: noCredentialless(g.navigator && g.navigator.userAgent),
	};
}

export const SAFARI_NOT_ISOLATED = "Safari can't run this machine here. Reload this page once; if it still doesn't start, update Safari (15.2 or newer) or use Chrome, Edge or Firefox on a computer. Alpine Linux (v86) works here.";

// First failing precondition, in the order a user can act on it; null when the runtime may load.
export function checkPreconditions(env) {
	if (!env.accepted) return { code: "NOT_ACCEPTED", message: "Accept the CheerpX licence notice to start this machine." };
	if (!env.secureContext) return { code: "NOT_ISOLATED", message: "This page isn't a secure context. Open Re:Vault over HTTPS." };
	if (!env.crossOriginIsolated && env.webkit) return { code: "NOT_ISOLATED", message: SAFARI_NOT_ISOLATED };
	if (!env.crossOriginIsolated) return { code: "NOT_ISOLATED", message: "This page isn't cross-origin isolated, which CheerpX needs. Reload Re:Vault; if it keeps happening, the server is missing its COOP/COEP headers." };
	if (!env.sharedArrayBuffer || !env.wasmSharedMemory) return { code: "NO_SHARED_MEMORY", message: "This browser doesn't provide shared WebAssembly memory (SharedArrayBuffer). CheerpX needs Chrome/Edge 92+, Firefox 79+ or Safari 15.2+. The v86 machines work everywhere." };
	if (!env.online) return { code: "OFFLINE", message: "You're offline. The CheerpX runtime and its disk are streamed from the network." };
	return null;
}

const RUNTIME_PATH = /^\/\d+\.\d+\.\d+\/cx\.esm\.js$/;
// https only, no credentials/query/fragment, /<semver>/cx.esm.js, and ONLY the licensed CDN host.
export function validateRuntimeUrl(value) {
	let url;
	try { url = new URL(value); } catch (e) { return { ok: false, reason: "The CheerpX runtime URL isn't a valid URL." }; }
	if (url.protocol !== "https:") return { ok: false, reason: "The CheerpX runtime URL must use https:." };
	if (url.username || url.password || url.search || url.hash) return { ok: false, reason: "The CheerpX runtime URL must not contain credentials, a query or a fragment." };
	if (!RUNTIME_PATH.test(url.pathname)) return { ok: false, reason: "The CheerpX runtime URL must end in /<version>/cx.esm.js." };
	if (url.hostname !== CHEERPX_RUNTIME_HOST) return { ok: false, reason: "CheerpX must be loaded from " + CHEERPX_RUNTIME_HOST + " (its licence forbids self-hosting)." };
	return { ok: true, url: url.href };
}

export function mapRuntimeError(err) {
	if (err instanceof LinuxError) return { code: err.code, message: err.message };
	const name = err && err.name ? err.name : "";
	const text = err && err.message ? err.message : String(err);
	if (name === "AbortError" || /\baborted\b/i.test(text)) return { code: "ABORTED", message: "The machine was stopped before CheerpX finished loading." };
	if (/SharedArrayBuffer|crossOriginIsolated|Cross-Origin-Opener-Policy/i.test(text)) return { code: "NOT_ISOLATED", message: "CheerpX couldn't start because this page isn't cross-origin isolated: " + text };
	if (/CheerpX initialization failed/i.test(text)) return { code: "RUNTIME_INIT_FAILED", message: text };
	if (/dynamically imported module|Failed to fetch|NetworkError|Importing a module script failed|Content Security Policy|timed out/i.test(text)) {
		return { code: "RUNTIME_LOAD_FAILED", message: "The CheerpX runtime couldn't be loaded (" + text + "). Check your connection; some networks block " + CHEERPX_RUNTIME_HOST + "." };
	}
	return { code: "RUNTIME_INIT_FAILED", message: text || "Unknown CheerpX error" };
}

const EXPECTED = ["Linux", "CloudDevice", "HttpBytesDevice", "IDBDevice", "OverlayDevice", "DataDevice"];
export function isCheerpxRuntime(ns) {
	if (!ns || typeof ns !== "object") return false;
	const exported = (v) => typeof v === "function" || (!!v && typeof v === "object" && typeof v.create === "function");
	return EXPECTED.every((k) => exported(ns[k]));
}

const inflight = new Map();
const loaded = new Map();
const defaultImporter = (url) => import(url);

export function loadRuntime(runtimeUrl, opts = {}) {
	const check = validateRuntimeUrl(runtimeUrl);
	if (!check.ok) return Promise.reject(new LinuxError("RUNTIME_LOAD_FAILED", check.reason));
	if (loaded.has(check.url)) return Promise.resolve(loaded.get(check.url));
	if (inflight.has(check.url)) return inflight.get(check.url);
	const timeoutMs = opts.timeoutMs || 30000;
	const importer = opts.importer || defaultImporter;
	const attempt = new Promise((resolve, reject) => {
		if (opts.signal && opts.signal.aborted) { reject(new LinuxError("ABORTED", "Runtime load aborted.")); return; }
		let settled = false;
		const finish = (err, ns) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			if (opts.signal) opts.signal.removeEventListener("abort", onAbort);
			if (ns) resolve(ns); else reject(err);
		};
		const timer = setTimeout(() => finish(new LinuxError("RUNTIME_LOAD_FAILED", "The CheerpX runtime didn't load within " + Math.round(timeoutMs / 1000) + " s. Check your connection.")), timeoutMs);
		const onAbort = () => finish(new LinuxError("ABORTED", "Runtime load aborted."));
		if (opts.signal) opts.signal.addEventListener("abort", onAbort, { once: true });
		importer(check.url).then(
			(ns) => (isCheerpxRuntime(ns) ? finish(null, ns) : finish(new LinuxError("RUNTIME_LOAD_FAILED", check.url + " did not export the CheerpX runtime API."))),
			(err) => { const m = mapRuntimeError(err); finish(new LinuxError(m.code, m.message, err)); }
		);
	});
	inflight.set(check.url, attempt);
	attempt.then((ns) => loaded.set(check.url, ns), () => {}).finally(() => inflight.delete(check.url));
	return attempt;
}
export function resetRuntimeCache() { inflight.clear(); loaded.clear(); }
